import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

import styled from 'styled-components';

import {  updateMultipleBuyingEmails } from '../../../store/slices/buyingEmails/buyingEmails';
import { updatePlatform, updatePlatforms } from '../../../store/slices/platforms/platforms';

import { Button, Checkbox, Divider, IconButton, Select, TextField, MenuItem, FormControl, FormHelperText, Tooltip, InputAdornment, CircularProgress, Toolbar, Popover } from "@mui/material";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { BuyingEmailFormSchema_noPasswordRequired, BuyingEmailFormSchema_passwordRequired } from '../../../validations/BuyingEmailValidations';
import PasswordOutlinedIcon from '@mui/icons-material/PasswordOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import gmailLogo from '../../../img/icons/gmail-logo.svg';
import outlookLogo from '../../../img/icons/outlook-logo.svg'
import ticketmasterLogo from '../../../img/icons/ticketmaster-logo.svg'
import liveNationLogo from '../../../img/icons/live-nation-logo.svg';
import ticketLogo from '../../../img/ticket-icon.svg';
import RandExp from 'randexp';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useLazyDecryptEmailPasswordQuery, useGetFullBuyingEmailDataForUserQuery, useLazyGetBuyingEmailWithPlatformsQuery } from '../../../services/buyingEmailApi';

import { useCreateBuyingEmailMutation, useUpdateEmailMutation } from '../../../services/buyingEmailApi';

import { updateBuyingEmail } from '../../../store/slices/buyingEmails/buyingEmails';
import { useDecryptPlatformPasswordMutation, useDeletePlatformMutation, useGetPlatformNamesQuery, useLazyGetPlatformsForUserQuery } from '../../../services/platformsApi';
import { selectCurrentUser } from '../../../features/auth/authSlice';
import BasicModalConfirmation from './../../../components/basicModalConfirmation';

import { parseToEasternTime } from '../../../utils/dates';
import BuyingEmailWizard from './BuyingEmailWizard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { copyToClipBoard } from '../../../utils';
import { useGetAccountsFastListQuery } from '../../../services/accountsApi';

const BuyingEmailContainer = styled.div`
  margin: 0;
  width: 850px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  background-color: var(--background-lightgray);
  border-radius: 2em;
  padding: 2em;
`
const EmailInputsDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1em 0;
  width: 100%;
`

const InputRow = styled.div`
  margin: 0 0 .5em 0;
  width: 100%;
  
  display: flex;
  white-space: nowrap;
  align-items: center;
  gap: 1em;
`

function CopyComponent({ textToCopy, copyResultText, disabled, id = "" }) {
  const [showCopy, setShowCopy] = useState(false);
  const copyButtonRef = useRef(null);

  return (
    <>
      <IconButton
        ref={copyButtonRef}
        disabled={disabled}
        color="primary"
        variant="contained"
        style={{ margin: '0 0 0 4px' }}
        onClick={() => {
          copyToClipBoard(textToCopy)
          setShowCopy(true)
        }}
      >
        <ContentCopyIcon
          id={"EmailCopylAnchor" + id}
        />
      </IconButton>

      <Popover
        id={"EmailCopylAnchor" + id}
        open={showCopy}
        anchorEl={copyButtonRef.current}
        onClose={() => setShowCopy(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <p style={{ margin: "0", padding: "0.5em", fontSize: "0.85rem", fontWeight: 700, color: 'var(--orange)' }} >
          {copyResultText || "Copied!"}
        </p>
      </Popover>
    </>
  )
}

function BuyingEmailEditMessage() {
  return <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: 'var(--orange)', padding: '1em', margin: '1em 0 0 0 ' }} >
    <p style={{ color: 'white', fontWeight: '600', fontSize: 'var(--p2)' }} >You are currently editing the Mailbox info, not the Box Office info.</p>
  </div>
}

function BoxOfficeEditMessage() {
  return <div style={{ width: '100%', display: 'flex', justifyContent: 'center', backgroundColor: 'var(--orange)', padding: '1em', margin: '0 0 1em 0 ' }} >
    <p style={{ color: 'white', fontWeight: '600', fontSize: 'var(--p2)' }} > You are currently editing the password to log into Box Office websites. </p>
  </div>
}

const BuyingEmailFooter = ({ fullBuyingEmail }) => {

  let createdByUser = fullBuyingEmail.createdByUser;

  let modifiedByUser = fullBuyingEmail.modifiedByUser;

  let createdOn = fullBuyingEmail.createdOn;
  let modifiedOn = fullBuyingEmail.modifiedOn;
  return (
    <div className="modal-footer" style={{ width: '100%' }}>
      {createdByUser &&
        <>
          <p style={{ color: "var(--grey-inactive)", margin: '0 0.5em 0 0' }} >Created by </p>
          <p style={{ margin: '0 1em 0 0' }}>
            {createdByUser} - {parseToEasternTime(createdOn)}</p>
        </>
      }

      {modifiedByUser &&
        <>
          <p style={{ color: "var(--grey-inactive)", margin: '0 0.5em 0 0' }} >Modified by </p>
          <p>{modifiedByUser} - {parseToEasternTime(modifiedOn)}</p>
        </>
      }
    </div>
  )
}

const EmailInputs = (
  {
    beeIsEditing,
    currentUser,
    localEmail,
    setLocalEmail,
    userId,
    users,
    editable,
    errors,
    onAssignedUserChange,
    onCheckboxChange,
    onEmailAddressChange,
    onEmailPasswordChange,
    passwordValue,
    showPassword,
    isEditing,
    handleClickShowPassword,
    decryptedEmail
  }
) => {

  const [userFocus, setUserFocus] = useState(false);

  // TODO: duplicated code
  function generatePassword() {

    // Regular expressions for each character group
    const upperRegEx = new RandExp('[A-Z]');
    const lowerRegEx = new RandExp('[a-z]');
    const numberRegEx = new RandExp('[0-9]');
    const alphanumericRegEx = new RandExp('[a-zA-Z0-9]{5}');

    // Generate one character for each group
    const upperChar = upperRegEx.gen();
    const lowerChar = lowerRegEx.gen();
    const numberChar = numberRegEx.gen();

    // Generate the remaining 5 characters
    const remainingChars = alphanumericRegEx.gen();

    // Combine and shuffle characters
    const passwordArray = [upperChar, lowerChar, numberChar, ...remainingChars.split('')];
    const shuffledArray = passwordArray.sort(() => Math.random() - 0.5);
    const password = shuffledArray.join('') + '!';

    return password
  }

  function onGeneratePasswordClick() {
    const password = generatePassword()

    setLocalEmail({ ...localEmail, mailboxPassword: password })
  }
  
  if (beeIsEditing) {
    return (
      // THIS MODAL IS FOR THE USER
      <EmailInputsDiv>

        <p>
          <Checkbox
            onChange={onCheckboxChange}
            defaultChecked
            checked={localEmail?.isActive}
            disabled={!editable}
          />
          <label>Active Buying Email</label>
        </p>

        <Divider style={{ width: "100%", margin: "0 0 1em 0" }} />

        <div style={{ margin: '0', display: 'flex', width: '100%' }} >

          <div style={{ margin: '0 1em 0 0', padding: '0.5em', backgroundColor: 'var(--amber)', borderRadius: '0.5em', display: 'flex', flexDirection: 'column' }} >
            <EmailOutlinedIcon style={{ color: '#fff', margin: '0 0 0.25em 0' }} />
            <img src={gmailLogo} alt="Gmail logo" width="24px" style={{ margin: '0 0 0.5em 0' }} />
            <img src={outlookLogo} alt="Gmail logo" width="24px" />
          </div>

          <div style={{ margin: '0', display: 'flex', flexDirection: 'column', width: '100%' }} >

            <div style={{ display: 'flex', margin: '0 0 0.5em 0' }} >
              <h5 style={{ fontWeight: '700', margin: '0 0.25em 0 0' }} >Buying Email</h5>
              <Tooltip
                title="Email used for all your EncoreHive related work. Not for personal use. Mailbox Password for logging into the mailbox of buying email."
                placement="top-start">
                <InfoOutlinedIcon color='primary' />
              </Tooltip>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }} >

              <div style={{ margin: "0 2em 0 0", width: "50%" }}>
                <label>Email Address</label>
                <TextField
                  onChange={onEmailAddressChange}
                  value={localEmail?.emailAddress}
                  onFocus={() => setUserFocus(true)}
                  onBlur={() => setUserFocus(false)}
                  disabled={!editable || isEditing} //A: if bee is editing, disable the field
                  error={errors["buyingEmail.emailAddress"] ? true : false}
                  helperText={errors["buyingEmail.emailAddress"]}
                  sx={{ width: isEditing ? "85%" : "100%" }}
                  inputProps={{ style: { border: 'none', backgroundColor: editable && !isEditing ? "white" : "var(--light-grey)", padding: "9px 13px", margin: 0 } }}
                  variant="outlined"
                />
                {isEditing ? <CopyComponent textToCopy={localEmail?.emailAddress} /> : null}
              </div>

              <div style={{ width: "50%" }} >
                <div>
                  <label> Mailbox Password </label>
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <TextField
                      error={errors["buyingEmail.mailboxPassword"] ? true : false}
                      onFocus={() => setUserFocus("buyingEmail")}
                      onBlur={() => setUserFocus(false)}
                      helperText={errors["buyingEmail.mailboxPassword"]}
                      onChange={onEmailPasswordChange}
                      value={passwordValue}
                      disabled={!editable || (!showPassword && isEditing) || (isEditing && beeIsEditing)} //A: if bee is editing, disable the field
                      sx={{ width: "100%" }}
                      InputProps={{ // Props applied to the Input element. It will be a FilledInput, OutlinedInput or Input component depending on the variant prop value.
                        endAdornment:
                          isEditing ?
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                              >
                                {showPassword
                                  ?
                                  <VisibilityOutlinedIcon color='primary' />
                                  :
                                  <VisibilityOffOutlinedIcon color='primary' />
                                }
                              </IconButton>
                            </InputAdornment>
                            : null,
                      }}
                      inputProps={ // Attributes applied to the input element.
                        {
                          style:
                          {
                            border: 'none',
                            backgroundColor: editable && !isEditing ? "white" : "var(--light-grey)",
                            padding: "9px 13px",
                            margin: 0
                          },
                        }
                      }
                      variant="outlined"
                      type={!isEditing ? "text" : showPassword && decryptedEmail ? 'text' : 'password'}
                    />
                    {
                      isEditing && beeIsEditing ?
                        <CopyComponent disabled={!showPassword} textToCopy={localEmail?.mailboxPassword} id={"EmailPassword"}></CopyComponent>
                        :
                        <Tooltip title="Generate Password" placement="top">
                          <div >
                            <IconButton disabled={!Boolean(editable) || (!showPassword && isEditing)} onClick={onGeneratePasswordClick} color='primary' sx={{ width: "42px", height: "42px" }}>
                              <PasswordOutlinedIcon />
                            </IconButton>
                          </div>
                        </Tooltip>
                    }
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>

        {
          userFocus && <BuyingEmailEditMessage />
        }

      </EmailInputsDiv>
    )
  }
  else {
    return (
      <EmailInputsDiv>
        <div style={{ margin: "0 0 1em 0", display: "flex", alignItems: "center" }}>

          <div style={{ margin: "0 2em 0 0", width: "50%" }} >
            <label> Assigned User  </label>
            <FormControl sx={{ display: "flex", flexDirection: "column" }} error={errors["buyingEmail.assignedUserID"] ? true : false}>
              <Select
                value={(beeIsEditing && currentUser?.id) ? currentUser?.id : localEmail.assignedUserID || userId || 'default'}
                onChange={onAssignedUserChange}
                disabled={!editable || beeIsEditing}
                sx={{ height: "2.5em", width: "100%", backgroundColor: editable ? "white" : "var(--light-grey)", padding: "9px 13px" }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      height: '15em'
                    },
                  },
                }}
              >
                <MenuItem value={'default'}> -- Select a User -- </MenuItem>
                {
                  beeIsEditing && currentUser ? <MenuItem value={currentUser.id}>{`${currentUser.firstName} ${currentUser.lastName}`}</MenuItem> : null
                }
                {
                  !beeIsEditing && users
                    .slice()
                    .filter(x => !x.isDeleted || x.id == (localEmail.assignedUserID || userId))
                    .sort((a, b) => a.fullName.localeCompare(b.fullName))
                    .map((user, index) => (
                      <MenuItem
                        key={index}
                        value={user?.id}
                      >

                        {`${user?.fullName}`} {user?.isDeleted ? '(deleted)' : '' }

                      </MenuItem>
                    ))
                }
              </Select>
              <FormHelperText>{errors["buyingEmail.assignedUserID"]}</FormHelperText>
            </FormControl>
          </div>

          <p>
            <Checkbox
              onChange={onCheckboxChange}
              defaultChecked
              checked={localEmail?.isActive}
              disabled={!editable}
            />
            <label>Active Buying Email</label>
          </p>

        </div>

        <Divider style={{ width: "100%", margin: "0 0 1em 0" }} />

        <div style={{ margin: '0', display: 'flex' }} >

          <div style={{ margin: '0 1em 0 0', padding: '0.5em', backgroundColor: 'var(--amber)', borderRadius: '0.5em', display: 'flex', flexDirection: 'column' }} >
            <EmailOutlinedIcon style={{ color: '#fff', margin: '0 0 0.25em 0' }} />
            <img src={gmailLogo} alt="Gmail logo" width="24px" style={{ margin: '0 0 0.5em 0' }} />
            <img src={outlookLogo} alt="Gmail logo" width="24px" />
          </div>

          <div style={{ margin: '0', display: 'flex', flexDirection: 'column', width: '100%' }} >

            <div style={{ display: 'flex', margin: '0 0 0.5em 0' }} >
              <h5 style={{ fontWeight: '700', margin: '0 0.25em 0 0' }} >Buying Email</h5>
              <Tooltip
                title="Email used for all your EncoreHive related work. Not for personal use. Mailbox Password for logging into the mailbox of buying email."
                placement="top-start">
                <InfoOutlinedIcon color='primary' />
              </Tooltip>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row' }} >

              <div style={{ margin: "0 2em 0 0", width: "50%" }}>
                <label>Email Address </label>
                <TextField
                  onFocus={() => setUserFocus(true)}
                  onBlur={() => setUserFocus(false)}
                  onChange={onEmailAddressChange}
                  value={localEmail?.emailAddress}
                  disabled={!editable}
                  error={errors["buyingEmail.emailAddress"] ? true : false}
                  helperText={errors["buyingEmail.emailAddress"]}
                  sx={{ width: isEditing ? "85%" : "100%" }}
                  inputProps={{ style: { border: 'none', backgroundColor: editable ? "white" : "var(--light-grey)", padding: "8px 12px", margin: 0 } }}
                  variant="outlined"
                />
                {isEditing && <CopyComponent textToCopy={localEmail?.emailAddress} />}
              </div>

              <div style={{ width: "50%" }} >
                <div>
                  <label> Mailbox Password </label>
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <TextField
                      onFocus={() => setUserFocus(true)}
                      onBlur={() => setUserFocus(false)}
                      error={errors["buyingEmail.mailboxPassword"] ? true : false}
                      helperText={errors["buyingEmail.mailboxPassword"]}
                      onChange={onEmailPasswordChange}
                      value={passwordValue}
                      disabled={!editable || (!showPassword && isEditing)}
                      sx={{ width: "100%" }}
                      InputProps={{ // Props applied to the Input element. It will be a FilledInput, OutlinedInput or Input component depending on the variant prop value.
                        endAdornment:
                          isEditing ?
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                              >
                                {showPassword
                                  ?
                                  <VisibilityOutlinedIcon color='primary' />
                                  :
                                  <VisibilityOffOutlinedIcon color='primary' />
                                }
                              </IconButton>
                            </InputAdornment>
                            : null,
                      }}
                      inputProps={ // Attributes applied to the input element.
                        {
                          style:
                          {
                            border: 'none',
                            backgroundColor: editable || showPassword ? "white" : "var(--light-grey)",
                            padding: "9px 13px",
                            margin: 0
                          },
                        }
                      }
                      variant="outlined"
                      type={!isEditing ? "text" : showPassword && decryptedEmail ? 'text' : 'password'}
                    />
                    {
                      isEditing &&
                      <CopyComponent disabled={!showPassword} textToCopy={localEmail?.mailboxPassword} id={"EmailPassword"}></CopyComponent>
                    }

                    <Tooltip title="Generate Password" placement="top">
                      <div >
                        <IconButton disabled={!Boolean(editable) || (!showPassword && isEditing)} onClick={onGeneratePasswordClick} color='primary' sx={{ width: "42px", height: "42px" }}>
                          <PasswordOutlinedIcon />
                        </IconButton>
                      </div>
                    </Tooltip>

                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>

        {
          userFocus && <BuyingEmailEditMessage />
        }

      </EmailInputsDiv>
    )
  }
}

const BuyingEmailForm = (props) => {
  const dispatch = useDispatch();

  let { disable, userId, emailId, onClose, onSave, onSuccess, onError, isLoading, title, beeIsEditing, currentEmail } = props;
  let isEditing = userId && emailId;
  const [getBuyingEmail, {data: buyingEmailApiRes, isLoading: getBuyingEmailIsLoading } ]= useLazyGetBuyingEmailWithPlatformsQuery();
  let defaultLocalEmail= { assignedUserID: userId, isActive: true, emailAddress: '', mailboxPassword: '' }
  
  const [localEmail, setLocalEmail] = useState(currentEmail || defaultLocalEmail);
  const [newPlatformForms, setNewPlatformForms] = useState([]);

  useEffect(() => {
    if(emailId && !currentEmail){
      getBuyingEmail({buyingEmailID: emailId})
    }
  }, [])

  //A: buyingEmailApiRes has some data and is not loading update localEmail
  useEffect(() => {
    if(buyingEmailApiRes){
      let aux= JSON.parse(JSON.stringify(buyingEmailApiRes[0]));
      let auxPlatforms= JSON.parse(JSON.stringify(buyingEmailApiRes[0].platformsCredentials));
      setLocalEmail(aux);
      setNewPlatformForms(auxPlatforms);
    }
  }, [buyingEmailApiRes])


  const { data: users} = useGetAccountsFastListQuery();
  const currentUser = useSelector(selectCurrentUser);
  const {data: platformNames } = useGetPlatformNamesQuery();
  const [TM_LN_ID, OtherId] = useMemo(() => {
    const tmLnId = platformNames?.find(platform => platform.name === "TM/LN")?.id;
    const otherId = platformNames?.find(platform => platform.name === "Other")?.id;
    return [tmLnId, otherId];
  }, [platformNames]);

  const [showPassword, setshowPassword] = useState(false);
  const [showPlatformPasswords, setShowPlatformPasswords] = useState([]);

  const [createBuyingEmail, { data: createBuyingEmailData, isLoading: loadingBuyingEmail }] = useCreateBuyingEmailMutation();
  const [editBuyingEmail, { data: editBuyingEmailData, isLoading: loadingEditBuyingEmail }] = useUpdateEmailMutation();

  const [initDecriptEmail, { data: decryptedEmail, isLoading: loadingEmailDecrypted }] = useLazyDecryptEmailPasswordQuery();

  const [deletePlatform, { isloading: loadingPlatformDeletion }] = useDeletePlatformMutation()
  const [decryptPlatformPassword, { isLoading: loadingPlaformPassword }] = useDecryptPlatformPasswordMutation()
  const { data: fullUserEmailsData, isLoading: loadingFullUserEmails, isError: userEmailsError } = useGetFullBuyingEmailDataForUserQuery(userId, { skip: !isEditing })
  const [getPlatforms] = useLazyGetPlatformsForUserQuery();
  let loading = Boolean(isLoading || loadingBuyingEmail || loadingEditBuyingEmail)
  const [editable, setEditable] = useState(!disable)

  const [errors, setErrors] = useState({});
  const [userSawEmailPassword, setUserSawEmailPassword] = useState(false);
  const [disabledTM_LN, setDisabledTM_LN] = useState(false);
  const [loadingPlatformIndex, setLoadingPlatformIndex] = useState(null);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(null);
  const [BEDeleteConfirmationIsOpen, setBEDeleteConfirmationIsOpen] = useState(false);
  const [BERestoreConfirmationIsOpen, setBERestoreConfirmationIsOpen] = useState(false);
  const [page, setPage] = useState(isEditing ? 2 : 1) // A: only open wizard on creation of new email

  const [userFocus, setUserFocus] = useState(false); //A: with this state we show a warning message.

  useEffect(() => {
    if (decryptedEmail) {
      console.log('decrypted password', decryptedEmail);
      let aux= JSON.parse(JSON.stringify(decryptedEmail.data));
      //sometimes this properties are being populated, this breaks the form structure
      //this is a workaround to avoid this issue
      delete aux.assignedUser;
      delete aux.modifiedByUser;
      delete aux.createdByUser

      setLocalEmail(aux);
    }
  }, [decryptedEmail])

  useEffect(() => {
    if (loading || localEmail.isDeleted) {
      setEditable(false);
    } else if (!loading && !disable) {
      setEditable(true);
    }
  }, [loading, disable, localEmail])

  useEffect(() => {
    if (fullUserEmailsData && !loadingFullUserEmails && !userEmailsError) {
      dispatch(updateMultipleBuyingEmails(fullUserEmailsData.data));
    }
  }, [fullUserEmailsData, loadingFullUserEmails, userEmailsError]);

  async function onEditBuyingEmailClick(editedData) {
    try {
      let res = await editBuyingEmail(editedData).unwrap();
      console.log("edit email res: ", JSON.stringify(res));
      let BE = { ...res.buyingEmail }
      delete BE.mailboxPassword
      dispatch(updateBuyingEmail(BE));

      try {
        let platformsRes = await getPlatforms(userId).unwrap();
        console.log(platformsRes)
        dispatch(updatePlatforms(platformsRes))
      } catch (error) {
        if (error && error.status == 401) return; //A: if unauthorized error, do nothing
        throw new Error("Error getting platforms error stringify: " + JSON.stringify(error) + " , error: " + error);
      }

      let successMessage = "Buying Email edited successfully"
      onSuccess && onSuccess(res, successMessage)
      onClose();
    } catch (error) {
      if (error.status == 401) return; //error managed on baseQueryWithReauth
      let errorMessage = error.data?.message || "Error editing Buying Email"
      onError && onError(error, errorMessage)
    }
  }

  async function onCreateBuyingEmail(data,errorHandler) {
    //send creation request
    // update datagrid
    // close modal
    // catch unauthorized error handled by the service

    try {
      let res = await createBuyingEmail(data).unwrap();
      console.log("creation response: ", res)  
      let successMessage = "Buying email created successfully"
      onSuccess && onSuccess(res, successMessage)
      onClose();
    } catch (error) {
      if (error.status == 401) return; //error managed on baseQueryWithReauth 
      console.error(error)
      let ErrorMessage = error.data?.message || "Error creating Buying Email"
      errorHandler && errorHandler(error, ErrorMessage)
    }
    //onClose();
  }

  function onSaveClick() {

    let finalObject = { buyingEmail: { ...localEmail }, platformCredentials: newPlatformForms };

    const errorHandler = (error, errorMessage)=>{
      setErrors({"buyingEmail.emailAddress" : errorMessage});
    }

    let schemma = BuyingEmailFormSchema_noPasswordRequired
    if (userSawEmailPassword || !isEditing) {
      schemma = BuyingEmailFormSchema_passwordRequired
    }
    schemma.validate(finalObject, { abortEarly: false }) // A: if the validation fails, the catch block will be executed, if not the then block will be executed
      .then(validEmail => {
        // A: only executed if the validation is successful
        console.log("validation: ", validEmail);
        if (isEditing) {
          onEditBuyingEmailClick(validEmail);
        } else {
          onCreateBuyingEmail(validEmail, errorHandler);
        }
      })
      .catch(err => {
        console.log("BuyingEmailForm, " + err)
        let errorObjects = err.inner.map((error) => {
          return { [error.params.path]: error.errors[0] }
        })
        let assignedErrors = Object.assign({}, ...errorObjects)
        console.error("BuyingEmailForm, validation errors: ", assignedErrors)
        setErrors(assignedErrors)
      })
  }

  function onCancel() {
    onClose()
  }

  function onAssignedUserChange(e) {
    setLocalEmail({ ...localEmail, assignedUserID: e.target.value })
  }

  function onCheckboxChange(e) {
    setLocalEmail({ ...localEmail, isActive: !localEmail.isActive })
  }

  function onEmailAddressChange(e) {
    setLocalEmail({ ...localEmail, emailAddress: e.target.value })
  }

  function onEmailPasswordChange(e) {
    setLocalEmail({ ...localEmail, mailboxPassword: e.target.value })
  }

  function onAddPlatform() {
    setNewPlatformForms([...newPlatformForms, { shortName: '' }])
    setShowPlatformPasswords([...showPlatformPasswords, newPlatformForms.length])
  }

  async function onDeleteEmail() {
    let BEIdToDelete = localEmail.id;
    let BEToDelete = {
      buyingEmail: {
        id: BEIdToDelete,
        isDeleted: true,
        isActive: false,
      },
      platformCredentials: []
    }
    console.log("init onDeleteEmail BEIdToDelete: " + BEIdToDelete + " newBE: " + JSON.stringify(BEToDelete));
    try {
      let res = await editBuyingEmail(BEToDelete).unwrap();
      let BE = { ...res.buyingEmail }
      delete BE.mailboxPassword
      dispatch(updateBuyingEmail(BE));
      dispatch(updatePlatforms(res.platformCredentials))
      let successMessage = "Buying Email edited successfully"
      onSuccess && onSuccess(res, successMessage)
      console.log("onDeleteEmail response: " + JSON.stringify(res));
    } catch (error) {
      console.log("onDeleteEmail error delete BE: " + JSON.stringify(error))
      if (error.status == 401) return; //error managed on baseQueryWithReauth
      throw error;
    }
  }

  async function onRestoreEmail() {
    let BEIdToRestore = localEmail.id;
    let BEToRestore = {
      buyingEmail: {
        id: BEIdToRestore,
        isDeleted: false,
      },
      platformCredentials: []
    }
    console.log("init onRestoreEmail BEIdToRestore: " + BEIdToRestore + " newBE: " + JSON.stringify(BEToRestore));
    try {
      let res = await editBuyingEmail(BEToRestore).unwrap();
      console.log("onRestoreEmail response: " + JSON.stringify(res));
      let BE = { ...res.buyingEmail }
      delete BE.mailboxPassword
      dispatch(updateBuyingEmail(BE));
      dispatch(updatePlatforms(res.platformCredentials))
      let successMessage = "Buying Email edited successfully"
      onSuccess && onSuccess(res, successMessage)
    } catch (error) {
      console.log("onRestoreEmail error: " + JSON.stringify(error))
      if (error.status == 401) return; //error managed on baseQueryWithReauth
      throw error;
    }
  }

  function onPlatformNameChange(event, index) {
    const newPlatformFormsCopy = [...newPlatformForms];
    newPlatformFormsCopy[index].platformId = event.target.value;

    if (event.target.value === TM_LN_ID) {
      setDisabledTM_LN(true);
    } else if (disabledTM_LN) {
      const tm_ln_selected = newPlatformFormsCopy.some(
        (platform) => platform.platformId === TM_LN_ID
      );

      if (!tm_ln_selected) {
        setDisabledTM_LN(false);
      }
    }

    setNewPlatformForms(newPlatformFormsCopy);
  };

  async function onDeletePlatform(index, platform) {
    setOpenConfirmationModal({ index, platform })
  };

  async function deletePlatformMutationWrapper(id) {
    try {
      // Execute the deletePlatform mutation.
      let res = await deletePlatform(id).unwrap();
      // Log the deleted platform and update the redux platform store.
      console.log("Deleted Platform: ", res);
      dispatch(updatePlatform(res));
    } catch (error) {
      // Catch 401 unauthorized error.
      if (error.status == 401) return; //error managed on baseQueryWithReauth 

      console.error(error);
      // Call onError callback and pass the BE error message or a custom error message.
      let errorMessage = error.data?.message || "Error deleting Buying Email";
      onError && onError(error, errorMessage);
      // Report the error to raygun.
      throw error;
    }
  }

  function deletePlatformCredetial(index, platform) {
    // This asynchronous function handles the deletion of a platform.

    // If the platform has an ID is because it exists in the DB, proceed with deletion.
    if (platform.id) {
      // Execute the deletePlatform mutation.
      deletePlatformMutationWrapper(platform.id);
    }
    // Update the newPlatformForms state by removing the deleted platform.
    setNewPlatformForms((prevState) =>
      prevState.filter((_, i) => i !== index)
    );

    // If the platform to be deleted has the same ID as TM_LN_ID, enable the TM_LN button.
    if (platform.platformId === TM_LN_ID) {
      setDisabledTM_LN(false);
    }

    // Update the show password platform passwords array when deleting a platform.
    // Create a new array as a copy of the current showPlatformPasswords array.
    let updatedArray = [...showPlatformPasswords];

    // Check if the current showPlatformPasswords array does not include the index+1,
    // and if it includes the index.
    if (!showPlatformPasswords.includes(index + 1) && showPlatformPasswords.includes(index)) {
      // If the condition is met, filter out the index from the updated array.
      updatedArray = updatedArray.filter(item => item !== index);
    } else {
      // If the condition is not met, adjust the indexes in the updated array.
      updatedArray = updatedArray.map((idx) => {
        // If the current index is greater than the given index,
        // decrease the current index by 1.
        if (idx > index) {
          return idx - 1;
        } else {
          // If the current index is not greater than the given index,
          // keep the current index unchanged.
          return idx;
        }
      });
    }
    // Set the updated array to showPlatformPasswords.
    setShowPlatformPasswords(updatedArray);
  }


  function onPlatformPasswordChange(e, index) {
    let editedArray = newPlatformForms.map((platform, idx) => {
      if (idx === index) {
        return { ...platform, platformPassword: e.target.value }
      }

      return platform
    })

    setNewPlatformForms(editedArray)
  }

  function onPlatformShortNameChange(e, index) {
    let editedArray = newPlatformForms.map((platform, idx) => {
      if (idx === index) {
        return { ...platform, shortName: e.target.value }
      }

      return platform
    })

    setNewPlatformForms(editedArray)
  }

  function generatePasswordFor(index) {
    console.log("generatePasswordFor", index);

    // Regular expressions for each character group
    const upperRegEx = new RandExp('[A-Z]');
    const lowerRegEx = new RandExp('[a-z]');
    const numberRegEx = new RandExp('[0-9]');
    const alphanumericRegEx = new RandExp('[a-zA-Z0-9]{5}');

    // Generate one character for each group
    const upperChar = upperRegEx.gen();
    const lowerChar = lowerRegEx.gen();
    const numberChar = numberRegEx.gen();

    // Generate the remaining 5 characters
    const remainingChars = alphanumericRegEx.gen();

    // Combine and shuffle characters
    const passwordArray = [upperChar, lowerChar, numberChar, ...remainingChars.split('')];
    const shuffledArray = passwordArray.sort(() => Math.random() - 0.5);
    const password = shuffledArray.join('') + '!';

    let EditedArray = newPlatformForms.map((platform, idx) => {
      if (idx === index) {
        return { ...platform, platformPassword: password };
      }

      return platform;
    });

    setNewPlatformForms(EditedArray);
  }


  function handleClickShowPassword() {
    console.log('CLICK in decryptEmailPassword');
    let passwordIsHidden = showPassword === false;
    console.log("passwordIsHidden", passwordIsHidden);
    if (passwordIsHidden) {
      initDecriptEmail(emailId);
      setUserSawEmailPassword(true);
    }
    setshowPassword(!showPassword)
  }

  async function handleShowPlatformPassword(index) {
    console.log('showPlatformPassword', index)
    let aux = []
    if (showPlatformPasswords.includes(index)) {
      aux = showPlatformPasswords.filter((i) => i !== index)
    } else {
      aux = [...showPlatformPasswords, index];
    }

    let exists = aux.includes(index)
    setShowPlatformPasswords(aux)

    let id = newPlatformForms[index].id
    if (id !== undefined && exists && id !== null) {
      try {
        setLoadingPlatformIndex(index)
        let res = await decryptPlatformPassword(id).unwrap();
        setNewPlatformForms(newPlatformForms.map((p) => {
          if (p.id === id) {
            return { ...p, platformPassword: res.password }
          } else {
            return p
          }
        }))
        setLoadingPlatformIndex(null)
      } catch (error) {
        if (error.status == 401) return; //error managed on baseQueryWithReauth 
        console.error(error)
        let ErrorMessage = "Error decrypting platform password"
        onError && onError(error, ErrorMessage)
      }
    }
  };

  let passwordValue = "**********";

  if (isEditing && showPassword) {
    passwordValue = localEmail?.mailboxPassword
  } else if (!isEditing) {
    passwordValue = localEmail?.mailboxPassword
  }
  if (page === 1) {
    return (
      <BuyingEmailWizard onNextClick={() => setPage(page + 1)} onCancelClick={onClose} />
    )
  }
  return (
    <BuyingEmailContainer>

      <BasicModalConfirmation
        title="Are you sure you want to delete this Box Office Password?"
        cancelLabel="No"
        confirmLabel="Yes"
        open={Boolean(openConfirmationModal)}
        handleClose={() => setOpenConfirmationModal(null)}
        onClick={() => deletePlatformCredetial(openConfirmationModal?.index, openConfirmationModal?.platform)}
      />

      <BasicModalConfirmation
        title="Are you sure you want to delete this Buying Email?"
        cancelLabel="No"
        confirmLabel="Yes"
        open={Boolean(BEDeleteConfirmationIsOpen)}
        handleClose={() => setBEDeleteConfirmationIsOpen(false)}
        onClick={() => onDeleteEmail()}
      />

      <BasicModalConfirmation
        title="Are you sure you want to restore this Buying Email?"
        cancelLabel="No"
        confirmLabel="Yes"
        open={Boolean(BERestoreConfirmationIsOpen)}
        handleClose={() => setBERestoreConfirmationIsOpen(false)}
        onClick={() => onRestoreEmail()}
      />

      {/* Title */}
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }} >
        <h4 style={{ fontWeight: "600", color: "var(--orange)" }} >{title ? title : "New Buying Email Details"}</h4>
        {!isEditing && <p> Page 2 of 2 </p>}
      </div>

      {/* Email form */}
      <EmailInputs
        beeIsEditing={beeIsEditing}
        currentUser={currentUser}
        localEmail={localEmail}
        setLocalEmail={setLocalEmail}
        userId={userId}
        users={users || []}
        editable={editable}
        errors={errors}
        onAssignedUserChange={onAssignedUserChange}
        onCheckboxChange={onCheckboxChange}
        onEmailAddressChange={onEmailAddressChange}
        onEmailPasswordChange={onEmailPasswordChange}
        passwordValue={passwordValue}
        showPassword={showPassword}
        isEditing={isEditing}
        handleClickShowPassword={handleClickShowPassword}
        decryptedEmail={decryptedEmail}
      />

      <Divider style={{ width: "100%", margin: "0 0 1em 0" }} />

      {/* Platforms section  */}

      <div style={{ display: 'flex', margin: '0 0 2em 0' }} >
        <div style={{ margin: '0 1em 0 0', padding: '0.5em', backgroundColor: 'var(--amber)', borderRadius: '0.5em', display: 'flex', flexDirection: 'column' }} >
          <img src={ticketLogo} alt="Ticket logo" width="24px" style={{ margin: '0 0 0.25em 0' }} />
          <img src={ticketmasterLogo} alt="TicketMaster logo" width="24px" style={{ margin: '0 0 0.5em 0' }} />
          <img src={liveNationLogo} alt="Live Nation logo" width="24px" />
        </div>

        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>

          <div style={{ display: 'flex', margin: '0 0 0.5em 0' }} >
            <h5 style={{ fontWeight: '700', margin: '0 0.25em 0 0' }} >Box Office Password</h5>
            <Tooltip
              title="Password is used for logging into Box Office accounts (where you buy tickets) with the above email address. This password is also used for new account creation and registrations."
              placement="top-start">
              <InfoOutlinedIcon color='primary' />
            </Tooltip>
          </div>

          {/* Platform forms */}
          {
            newPlatformForms.map((platform, index) => {
              let disableOtherEdition = Boolean(platform.id) && platform.platformId === OtherId
              let isTMLN = platform.platformId === TM_LN_ID
              let disableShortNameField = isTMLN || !editable || disableOtherEdition
              const platformPasswordError = errors[`platformCredentials[${index}].platformPassword`];

              let platformPasswordValue = "";

              if (showPlatformPasswords.includes(index)) {
                platformPasswordValue = platform.platformPassword
              } else if (isEditing && Boolean(platform.id)) {
                platformPasswordValue = "*********"
              } else if (platform.platformPassword) platformPasswordValue = platform.platformPassword

              return (
                <div key={index} style={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}>

                  <div style={{ display: "flex", flexDirection: "column", width: "250px", margin: "0 2em 0 0" }}>
                    <label> Website </label>
                    <FormControl sx={{ display: "flex", flexDirection: "column" }} error={errors[`platformCredentials[${index}].platformId`] ? true : false}>
                      <Select
                        onFocus={() => setUserFocus(true)}
                        onBlur={() => setUserFocus(false)}
                        value={platform.platformId || "default"}
                        disabled={!editable || disableOtherEdition}
                        sx={{ height: "2.5em", backgroundColor: (!editable || disableOtherEdition) ? "var(--light-grey)" : "white", padding: "9px 13px" }}
                        onChange={(e) => onPlatformNameChange(e, index)}
                      >
                        <MenuItem value={'default'}> -- Select a Platform -- </MenuItem>
                        {
                          platformNames?.map((platformName) => {
                            return (
                              <MenuItem
                                key={platformName.id}
                                value={platformName.id}
                                disabled={
                                  platformName.id === TM_LN_ID &&
                                  (newPlatformForms.some((platform) => platform.platformId === TM_LN_ID) ||
                                    disabledTM_LN)
                                }
                              >
                                {platformName.name}
                              </MenuItem>
                            )
                          })
                        }
                      </Select>
                      <FormHelperText>{errors[`platformCredentials[${index}].platformId`]}</FormHelperText>
                    </FormControl>
                  </div>

                  <div style={{ width: "325px", display: "flex", flexDirection: "row", alignItems: "center", margin: "0 1.5em 0 0" }}>
                    <div style={{ display: "flex", flexDirection: "column", margin: "0 0.5em 0 0" }}>
                      <label> Box Office Password </label>
                      <div style={{ display: "flex", flexDirection: "row" }}>
                        <TextField
                          required
                          onFocus={() => setUserFocus(true)}
                          onBlur={() => setUserFocus(false)}
                          error={Boolean(platformPasswordError)}
                          helperText={platformPasswordError}
                          value={platformPasswordValue}
                          onChange={(e) => onPlatformPasswordChange(e, index)}
                          disabled={!editable || disableOtherEdition}
                          sx={{ width: "100%", margin: "0 0.5em 0 0" }}
                          inputProps={{ style: { border: 'none', backgroundColor: (!editable || disableOtherEdition) ? "var(--light-grey)" : "white", padding: "9px 13px", margin: 0 } }}
                          variant="outlined"
                          InputProps={{ // Props applied to the Input element. It will be a FilledInput, OutlinedInput or Input component depending on the variant prop value.
                            endAdornment:
                              isEditing ?
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => handleShowPlatformPassword(index)}
                                    edge="end"
                                  >
                                    {showPlatformPasswords.includes(index) ? (
                                      <VisibilityOutlinedIcon color="primary" />
                                    ) : (
                                      <VisibilityOffOutlinedIcon color="primary" />
                                    )}
                                  </IconButton>
                                </InputAdornment>

                                : null,
                          }}
                          type={!isEditing ? "text" : (showPlatformPasswords.includes(index) && loadingPlatformIndex !== index) ? 'text' : 'password'}
                        />

                        {
                          isEditing ?
                            <CopyComponent disabled={!showPlatformPasswords.includes(index)} textToCopy={platformPasswordValue} id={index} /> : null
                        }
                        <Tooltip title="Generate Password" placement="top">
                          <div >
                            <IconButton disabled={!Boolean(editable) || disableOtherEdition || !showPlatformPasswords.includes(index)} onClick={() => generatePasswordFor(index)} color='primary' sx={{ width: "42px", height: "42px" }}>
                              <PasswordOutlinedIcon />
                            </IconButton>
                          </div>
                        </Tooltip>


                      </div>
                    </div>
                  </div>

                  <div style={{ width: "110px", display: "flex", flexDirection: "row", alignItems: "flex-end" }}>
                    {/* Disable short name on TM/LN */}
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", margin: "0 0.5em 0 0" }}>
                      <label style={{ whiteSpace: "nowrap" }}> Short Name </label>
                      <TextField
                        onFocus={() => setUserFocus(true)}
                        onBlur={() => setUserFocus(false)}
                        value={platform.shortName}
                        onChange={(e) => onPlatformShortNameChange(e, index)}
                        disabled={disableShortNameField}
                        variant="outlined"
                        sx={{ backgroundColor: (disableShortNameField) ? "var(--light-grey)" : "white" }}
                        inputProps={{ style: { border: 'none', backgroundColor: disableShortNameField ? "var(--light-grey)" : "white", padding: "9px 13px", margin: 0 } }}
                      />
                    </div>

                    {
                      beeIsEditing && Boolean(platform.id) ? null :
                        <Tooltip title="Delete" placement="top">
                          <IconButton onClick={() => onDeletePlatform(index, platform)} disabled={!Boolean(editable)} color='primary' sx={{ width: "42px", height: "42px" }}>
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        </Tooltip>

                    }
                  </div>
                </div>
              )
            })
          }

        </div>

      </div>

      {userFocus && <BoxOfficeEditMessage />}

      {/* buttons */}
      <InputRow style={{ justifyContent: "space-between" }}>

        <div>
          <Button variant="contained" sx={{ marginRight: '2em' }} disabled={!Boolean(editable)} onClick={onAddPlatform}> Add Box Office Password </Button>
          {
            (!beeIsEditing && isEditing) ?
              localEmail.isDeleted ?
                <Tooltip title="Restore the Buying Email" placement="top">
                  <Button
                    variant="outlined"
                    onClick={() => setBERestoreConfirmationIsOpen(true)}
                    style={{ width: '100px' }}
                  >
                    restore
                  </Button>
                </Tooltip>
                :
                <Tooltip title="Delete the Buying Email" placement="top">
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={!Boolean(editable)}
                    onClick={() => setBEDeleteConfirmationIsOpen(true)}
                    style={{ width: '100px' }}
                  >
                    Delete
                  </Button>
                </Tooltip>
              : null
          }
        </div>

        <div>
          <Button variant="contained" onClick={onCancel} color='secondary' sx={{ width: "100px", margin: "0 2em" }} > Cancel </Button>
          <Button variant="contained" disabled={!Boolean(editable)} onClick={onSaveClick} sx={{ width: "100px" }} > {loading ? <CircularProgress size={30} /> : "Save"} </Button>
        </div>
      </InputRow>

      {
        localEmail && !beeIsEditing && isEditing &&
        <BuyingEmailFooter
          fullBuyingEmail={localEmail}
        />
      }

    </BuyingEmailContainer>
  );
}

export default BuyingEmailForm;